import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

import { PackageListItem } from '../../interfaces/application-package';
import { NotifierService } from '../../shared/notifier.service';
import { TailorService } from '../tailor.service';

/**
 * Compact history table for finalized/draft tailored CV packages. When a
 * `subjectUserId` is set it lists that consultant's packages; otherwise it
 * falls back to the whole client's packages (`clientId`) so the Tailored tab
 * can browse without first picking a user, exactly like the Standard tab.
 */
@Component({
  selector: 'bst-tailored-cv-list',
  templateUrl: './tailored-cv-list.component.html',
  styleUrls: ['./tailored-cv-list.component.scss'],
  standalone: false,
})
export class TailoredCvListComponent implements OnChanges, OnDestroy {

  @Input() public subjectUserId: number | null = null;
  @Input() public clientId: number | null = null;

  public items: PackageListItem[] = [];
  public loading = false;
  private sub?: Subscription;

  constructor(
    private readonly tailor: TailorService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
    private readonly notifier: NotifierService,
    public readonly router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subjectUserId'] || changes['clientId']) {
      this.reload();
    }
  }

  /** Show the User column only in client-wide mode (no consultant picked). */
  public get showUserColumn(): boolean {
    return !this.subjectUserId;
  }

  private reload(): void {
    if (this.subjectUserId) {
      this.load(this.tailor.listForUser(this.subjectUserId));
    } else if (this.clientId) {
      this.load(this.tailor.listForClient(this.clientId));
    } else {
      this.sub?.unsubscribe();
      this.items = [];
      this.loading = false;   // never leave the spinner stuck if inputs go null mid-load
    }
  }

  private load(source: Observable<PackageListItem[]>): void {
    this.loading = true;
    this.items = [];              // drop stale rows immediately
    this.sub?.unsubscribe();      // cancel any in-flight load (fast switch)
    this.sub = source.subscribe({
      next: (items) => {
        // Submitted drafts are the queue management actually works from, so
        // they sort above everything else; within each group the existing
        // recency order (created_at desc, from the backend) is preserved.
        items.sort((a, b) => Number(!!b.submitted_at) - Number(!!a.submitted_at));
        this.items = items;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.danger(
          this.translate.instant('cv.errors.listFailed') || 'Could not load tailored CVs.',
          this.translate.instant('cv.errors.title') || 'Error'
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Open a package's tailor workspace from a row click (action buttons stopPropagation). */
  public openRow(item: PackageListItem): void {
    this.router.navigate(['/cv-generator/tailor', item.id]);
  }

  public vacancyLabel(item: PackageListItem): string {
    if (item.is_pasted_vacancy) {
      return this.translate.instant('cv.tailored.pastedVacancy');
    }
    if (item.vacancy_title) {
      return item.vacancy_title;
    }
    return item.language ? item.language.toUpperCase() : '';
  }

  public downloadUrl(id: number): string {
    return this.tailor.downloadUrl(id, 'cv');
  }

  public letterDownloadUrl(id: number): string {
    return this.tailor.downloadUrl(id, 'letter');
  }

  public statusForCv(item: PackageListItem): string {
    return item.cv_status;
  }

  public statusForLetter(item: PackageListItem): string {
    return item.letter_status;
  }

  /** Hand a submitted package back to the consultant (privileged only; server 422s an already-finalized package). */
  public returnWork(item: PackageListItem): void {
    this.tailor.returnWork(item.id).subscribe({
      next: (p) => { item.submitted_at = p.submitted_at ?? null; },
      error: () => this.toastr.danger(
        this.translate.instant('cv.tailor.submit.returnFailed'),
        this.translate.instant('error'),
      ),
    });
  }

  /**
   * True when the CRM link did not happen, which is the only case worth a chip
   * here: a consultant submitted or management approved, and no CRM row
   * followed. `linked` is the expected outcome and stays silent.
   */
  public crmIssue(item: PackageListItem): boolean {
    return !!item.crm_sync_state && item.crm_sync_state !== 'linked';
  }

  /** Delete an in-progress (not submitted, not finalized) package so the consultant can start clean. */
  public async discard(item: PackageListItem): Promise<void> {
    const ok = await this.notifier.confirm(
      'cv.tailored.discardConfirmTitle',
      this.translate.instant('cv.tailored.discardConfirmSubtitle'),
      'cv.tailored.discard',
      'danger',
    );
    if (!ok) { return; }

    this.tailor.discard(item.id).subscribe({
      next: () => { this.items = this.items.filter(i => i.id !== item.id); },
      error: () => this.toastr.danger(
        this.translate.instant('cv.tailored.discardFailed'),
        this.translate.instant('error'),
      ),
    });
  }

  /** Only an in-progress package (no submit, no finalized artifact, nothing generating) can be discarded — mirrors the backend's own gate. */
  public canDiscard(item: PackageListItem): boolean {
    return !item.submitted_at
      && item.cv_status !== 'finalized' && item.letter_status !== 'finalized'
      && item.cv_status !== 'generating' && item.letter_status !== 'generating';
  }

  public chipStatus(artifactStatus: string): string {
    switch (artifactStatus) {
      case 'finalized': return 'success';
      case 'draft':     return 'basic';
      case 'failed':    return 'danger';
      case 'generating': return 'info';
      default:          return 'basic';
    }
  }
}
